<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { VueCal, type VueCalEvent, type VueCalSpecialHours } from 'vue-cal';
import { useI18n } from 'vue-i18n';
import type { RoutePlanCalendarEvent } from '@/utils/route-plan-calendar-events';
import 'vue-cal/style.css';

// Dedicated daily calendar for the route-plan board. Intentionally NOT shared with the shifts
// Calendar v2 grid: the two serve different flows and backend models, so changes to one must
// never affect the other.
export interface RoutePlanCalendarSchedule {
  id: number;
  label: string;
}

interface Props {
  events: RoutePlanCalendarEvent[];
  schedules: RoutePlanCalendarSchedule[];
  viewDate: string;
  specialHours?: VueCalSpecialHours;
  loading?: boolean;
}

const props = defineProps<Props>();

const { t } = useI18n();

const emit = defineEmits<{
  eventClick: [assignmentId: number | undefined],
  cellClick: [payload: { date: Date, scheduleId: number | null }],
  // Dragged a placed visit to a new caregiver column / time.
  eventDrop: [payload: { assignmentId: number, caregiverId: number, arrivalTime: string }],
  // Dropped a pending visit (dragged from the left panel) onto a slot.
  externalDrop: [payload: { visitId: number, caregiverId: number, arrivalTime: string }],
  // Dragged a lunch break to a new time (the owner caregiver never changes).
  breakDrop: [payload: { caregiverId: number, startTime: string }],
  // Dragged an existing shift to a new caregiver column / time (proposal, persisted on apply).
  shiftDrop: [payload: { shiftId: number, caregiverId: number, startTime: string }],
  // Clicked a movable existing shift → open its edit dialog.
  shiftClick: [shiftId: number],
  // vue-cal delivered a drop it lost track of: nothing was saved, but the block may have
  // moved visually — the board redraws the calendar from server state.
  dropAborted: [],
  // Native drag lifecycle of an assignment block, so the board can offer the pending
  // panel as an unassign drop target while it is mid-air.
  assignmentDragStart: [assignmentId: number],
  assignmentDragEnd: [],
  // Clicked a caregiver's column heading → edit their working hours in place.
  scheduleClick: [caregiverId: number],
}>();

const HOUR_CELL_HEIGHT = 40;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
// Drops snap to the half hour: dragging near 12:00 lands ON 12:00, not 11:55/12:05.
const SNAP_MINUTES = 30;
const TIME_PAD = 2;
// The viewport opens at 08:00 so the working day is visible without scrolling.
const INITIAL_SCROLL_HOUR = 8;

// RoutePlanCalendarEvent is structurally a VueCalEvent whose start/end are plain strings.
const vueCalEvents = computed(() => props.events as unknown as VueCalEvent[]);

// A Date object (local construction, no string parsing) — vue-cal 5 RC fails to parse string
// view dates on its view-switch path and silently keeps the previous range.
const viewDateAsDate = computed(() => {
  const [year, month, day] = props.viewDate.split('-').map(Number);

  return new Date(year, month - 1, day);
});

// Tonal look: tinted background with the caregiver color as text.
function eventStyle(event: { colorTint?: string, colorHex?: string }) {
  if (!event.colorTint) return undefined;

  return { backgroundColor: event.colorTint, color: event.colorHex };
}

const gridRoot = ref<HTMLElement>();

// Disabling a Vuetify overlay force-closes it, so an open hover tooltip doesn't stay
// stuck on screen while its block is being dragged.
const dragging = ref(false);
// Pairs a deferred drag start with its drag (see onNativeDragStart); bumped by dragend/unmount.
let dragSequence = 0;

// The grid always opens at 08:00. vue-cal resets its scroll on navigation, so the target offset
// is pinned directly on the DOM, requeried every frame until the transition window is over.
const WORKDAY_SCROLL_TARGET = INITIAL_SCROLL_HOUR * HOUR_CELL_HEIGHT;
const WORKDAY_PIN_FRAMES = 72;

function pinWorkdayScroll(framesLeft = WORKDAY_PIN_FRAMES) {
  gridRoot.value?.querySelectorAll('.vuecal__scrollable').forEach((element) => {
    if (element.scrollHeight > element.clientHeight) {
      element.scrollTop = preservedScroll?.top ?? WORKDAY_SCROLL_TARGET;
    }
    if (preservedScroll) element.scrollLeft = preservedScroll.left;
  });
  if (framesLeft > 0) requestAnimationFrame(() => pinWorkdayScroll(framesLeft - 1));
}

function onReady() {
  pinWorkdayScroll();
}

// vue-cal's cell mousedown registers document-level mousemove/mouseup listeners (its
// drag-to-create machinery) and only removes them from that mouseup — never on unmount. The
// board remounts this calendar on every plan change, so a press on empty grid whose mouseup
// the browser never delivers (a native drag took over) leaves a listener that throws on every
// mousemove once the cell is gone. Track such presses and let vue-cal's own cleanup run
// before the instance is torn down.
let cellPressPending = false;

function onGridMouseDown(e: MouseEvent) {
  const target = e.target instanceof Element ? e.target : null;
  // Only a real mouseup clears the flag: a later block press (whose own mouseup a native drag
  // may also swallow) must not hide a cell press that is still waiting for its cleanup.
  if (target?.closest('.vuecal__cell') && !target.closest('.vuecal__event')) cellPressPending = true;
}

function onDocumentMouseUp() {
  cellPressPending = false;
}

onMounted(() => {
  document.addEventListener('mouseup', onDocumentMouseUp, true);
});

onBeforeUnmount(() => {
  // A deferred drag start must not emit from an unmounted instance (remount mid-drag).
  dragSequence += 1;
  document.removeEventListener('mouseup', onDocumentMouseUp, true);
  if (cellPressPending) {
    cellPressPending = false;
    document.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
  }
  const element = gridRoot.value?.querySelector('.vuecal__scrollable');
  if (element) preservedScroll = { left: element.scrollLeft, top: element.scrollTop };
});

watch(() => props.viewDate, () => pinWorkdayScroll());

// vue-cal only sets cell.schedule when its pointerdown captured the column, so the clicked
// element's data-schedule attribute is the fallback.
function scheduleIdFromDom(e?: Event): number | null {
  const domSchedule = (e?.target as Element | null)
    ?.closest?.('[data-schedule]')
    ?.getAttribute('data-schedule');

  return domSchedule ? Number(domSchedule) : null;
}

function onCellClick({ cell, cursor, e }: {
  cell: { schedule?: number | string | null },
  cursor: { date: Date },
  e?: Event,
}) {
  const scheduleId = (cell.schedule as number | null) ?? scheduleIdFromDom(e);
  emit('cellClick', { date: cursor.date, scheduleId });
}

// The lunch break is an informational overlay: label inline, detail in a hover tooltip.
// `class` is optional because vue-cal's drag ghost re-renders this slot with a bare event object.
function isBreak(event: { class?: string }): boolean {
  return !!event.class && event.class.includes('route-plan-break');
}

// Assignment blocks open the visit editor; movable existing shifts open the shift editor;
// the break only shows its tooltip.
function onEventActivate(event: { assignmentId?: number, existingShiftId?: number, draggable?: boolean }) {
  if (event.assignmentId !== undefined) {
    emit('eventClick', event.assignmentId);

    return;
  }
  if (event.existingShiftId !== undefined && event.draggable) {
    emit('shiftClick', event.existingShiftId);
  }
}

// --- drag & drop --------------------------------------------------------------------------

function hmFromMinutes(rawMinutes: number): string {
  // Cap AFTER rounding: a drop at the very bottom would otherwise round up to "24:00",
  // which the backend parses as the NEXT day's midnight.
  const total = Math.min(
    Math.round(rawMinutes / SNAP_MINUTES) * SNAP_MINUTES,
    (HOURS_PER_DAY * MINUTES_PER_HOUR) - SNAP_MINUTES,
  );
  const hh = String(Math.floor(total / MINUTES_PER_HOUR)).padStart(TIME_PAD, '0');
  const mm = String(total % MINUTES_PER_HOUR).padStart(TIME_PAD, '0');

  return `${hh}:${mm}`;
}

function hm(date: Date): string {
  return hmFromMinutes(date.getHours() * MINUTES_PER_HOUR + date.getMinutes());
}

// Fired AFTER vue-cal committed the drop: event.start and event.schedule are already updated
// (the .d.ts also declares `event-drop`, but this RC only emits `event-dropped` at runtime).
// The board persists the move; a rejected move reverts on the next refetch.
function toCaregiverId(rawSchedule: number | string | null | undefined): number | null {
  if (rawSchedule === undefined || rawSchedule === null) return null;
  const id = Number(rawSchedule);

  return Number.isNaN(id) ? null : id;
}

// A dragged lunch only changes its TIME; the refetch snaps it back to its own column if
// it was dropped elsewhere.
function handleBreakDrop(event: { breakCaregiverId?: number, start: Date }): boolean {
  if (event.breakCaregiverId === undefined) return false;

  emit('breakDrop', { caregiverId: event.breakCaregiverId, startTime: hm(event.start) });

  return true;
}

function handleShiftDrop(
  event: { existingShiftId?: number, start: Date, schedule?: number | string | null },
  cell: { schedule?: number | string | null },
): boolean {
  if (event.existingShiftId === undefined) return false;

  const caregiverId = toCaregiverId(cell?.schedule ?? event.schedule);
  if (caregiverId !== null) {
    emit('shiftDrop', {
      shiftId: event.existingShiftId, caregiverId, startTime: hm(event.start),
    });
  }

  return true;
}

function handleAssignmentDrop(
  event: { assignmentId?: number, start: Date, schedule?: number | string | null },
  cell: { schedule?: number | string | null },
) {
  if (event.assignmentId === undefined) return;

  const caregiverId = toCaregiverId(cell?.schedule ?? event.schedule);
  if (caregiverId === null) return;

  emit('eventDrop', {
    assignmentId: event.assignmentId, caregiverId, arrivalTime: hm(event.start),
  });
}

function onEventDropped({ event, cell }: {
  event?: {
    assignmentId?: number, breakCaregiverId?: number, existingShiftId?: number, start: Date,
    schedule?: number | string | null,
  },
  cell: { schedule?: number | string | null },
}) {
  // The RC can emit a drop without the dragged event (lost internal drag state). Nothing to
  // persist, but the block may sit wherever it was dropped — signal the board to redraw.
  if (!event) {
    emit('dropAborted');

    return;
  }
  if (handleBreakDrop(event)) return;
  if (handleShiftDrop(event, cell)) return;
  handleAssignmentDrop(event, cell);
}

// vue-cal makes its own event element draggable (an ancestor of our slot button), so the
// dragged node is found by looking DOWN from the drag target for the stamped id. Only a
// SINGLE stamped descendant is unambiguous — if the target wraps several blocks (e.g. a
// stacked cell), picking the first would unassign a visit the user never touched, so fall
// back to closest(): the drag began inside one specific block or not at all.
function stampedAssignmentId(target: EventTarget | null): number | null {
  if (!(target instanceof Element)) return null;
  const below = target.querySelectorAll('[data-assignment-id]');
  const stamped = below.length === 1 ? below[0] : target.closest('[data-assignment-id]');
  if (!stamped) return null;

  const id = Number(stamped.getAttribute('data-assignment-id'));

  return Number.isFinite(id) ? id : null;
}

// Native drag lifecycle of a calendar block. Chrome re-hit-tests the mouse-down point right
// after `dragstart` and, if anything moved out from under it, cancels the drag with a
// SYNCHRONOUS `dragend` (Blink StartDrag: "we owe m_dragSrc a DRAGEND event"). Every reactive
// side effect of a drag start (closing tooltips, lighting the pending panel) is therefore
// deferred to the next task, and skipped when the drag has already ended by then. The counter
// pairs each deferred start with its drag: a dragend (or a remount) invalidates it.
function onNativeDragStart(e: DragEvent) {
  // vue-cal cancels drags it doesn't own (text-node targets, mid-resize): no drag, no dragend.
  if (e.defaultPrevented) return;

  const sequence = ++dragSequence;
  const assignmentId = stampedAssignmentId(e.target);
  window.setTimeout(() => {
    if (sequence !== dragSequence) return;
    dragging.value = true;
    if (assignmentId !== null) emit('assignmentDragStart', assignmentId);
  }, 0);
}

// vue-cal hides the dragged original in a setTimeout(0) queued from `dragstart` and only
// un-hides it in `dragend`. When Chrome cancels the drag at start, that dragend runs BEFORE
// the timeout, so the block is hidden afterwards and stays invisible until the next remount.
// Stripping the class from a later timeout wins regardless of the order the two arrive in.
function unhideStuckBlocks() {
  gridRoot.value?.querySelectorAll('.vuecal__event--dragging-original')
    .forEach((element) => element.classList.remove('vuecal__event--dragging-original'));
}

function onNativeDragEnd() {
  dragSequence += 1;
  dragging.value = false;
  emit('assignmentDragEnd');
  window.setTimeout(unhideStuckBlocks, 0);
}

// External HTML5 drag from the pending panel: resolve the caregiver column from the DOM and
// the time from the pointer's Y offset inside the day grid (1 hour == HOUR_CELL_HEIGHT px).
function onExternalDragOver(e: DragEvent) {
  if (e.dataTransfer?.types.includes('application/x-route-plan-visit')) e.preventDefault();
}

function timeFromDropY(e: DragEvent): string | null {
  const cell = (e.target as Element | null)?.closest?.('.vuecal__cell');
  if (!cell) return null;

  const rect = cell.getBoundingClientRect();
  const minutes = ((e.clientY - rect.top) / HOUR_CELL_HEIGHT) * MINUTES_PER_HOUR;
  const clamped = Math.max(0, Math.min(minutes, (HOURS_PER_DAY * MINUTES_PER_HOUR) - 1));

  return hmFromMinutes(clamped);
}

function onExternalDrop(e: DragEvent) {
  const visitId = Number(e.dataTransfer?.getData('application/x-route-plan-visit'));
  const caregiverId = scheduleIdFromDom(e);
  const arrivalTime = timeFromDropY(e);
  if (!visitId || caregiverId === null || !arrivalTime) return;

  e.preventDefault();
  emit('externalDrop', { visitId, caregiverId, arrivalTime });
}
</script>

<script lang="ts">
// Scroll position carried across the remounts the board triggers to flush vue-cal's stale
// event cache (see route-plan-board's calendarVersion): module scope survives the instance
// teardown, so the fresh instance resumes where the previous one was instead of snapping
// back to 00:00 / the leftmost caregiver column.
let preservedScroll: { left: number, top: number } | null = null;
</script>
<template>
  <div
    ref="gridRoot"
    class="route-plan-calendar"
    :style="{ '--route-plan-hour-height': `${HOUR_CELL_HEIGHT}px` }"
    @dragover="onExternalDragOver"
    @drop="onExternalDrop"
    @dragstart="onNativeDragStart"
    @dragend="onNativeDragEnd"
    @mousedown.capture="onGridMouseDown"
  >
    <!-- Fixed footprint: an inactive v-progress-linear collapses to 0px, and a grid that
         jumps 4px while a request is in flight can move a block out from under a press. -->
    <div class="route-plan-calendar__progress">
      <v-progress-linear
        :active="loading"
        indeterminate
        color="primary"
      />
    </div>
    <div>
      <vue-cal
        view="day"
        :views="['day']"
        :events="vueCalEvents"
        :schedules="schedules"
        :special-hours="specialHours"
        :view-date="viewDateAsDate"
        :stack-events="true"
        :editable-events="{ drag: true, resize: false, create: false, delete: false }"
        :snap-to-interval="SNAP_MINUTES"
        :time-from="0"
        :time-to="HOURS_PER_DAY * MINUTES_PER_HOUR"
        :time-step="MINUTES_PER_HOUR"
        :time-cell-height="HOUR_CELL_HEIGHT"
        :title-bar="false"
        :views-bar="false"
        locale="es"
        @ready="onReady"
        @cell-click="onCellClick"
        @event-dropped="onEventDropped"
      >
        <template #schedule-heading="{ schedule }">
          <button
            type="button"
            class="route-plan-calendar__schedule-name"
            :title="schedule.label"
            @click="emit('scheduleClick', Number(schedule.id))"
          >
            {{ schedule.label }}
            <v-icon
              icon="mdi-pencil-outline"
              size="x-small"
              class="route-plan-calendar__schedule-edit-icon"
            />
          </button>
        </template>
        <template #event="{ event }">
          <button
            type="button"
            class="route-plan-calendar__event"
            :style="eventStyle(event)"
            :data-assignment-id="event.assignmentId"
            @click.stop="onEventActivate(event)"
          >
            <!-- Short blocks fit one line only, so name and time share it; taller blocks keep
                 the time on its own line. -->
            <template v-if="!isBreak(event)">
              <span
                v-if="event.compact && event.content"
                class="route-plan-calendar__event-line"
              >
                <span class="font-weight-bold text-truncate">{{ event.title }}</span>
                <span class="text-caption">{{ event.content }}</span>
              </span>
              <template v-else>
                <span class="font-weight-bold text-truncate">
                  {{ event.title }}
                </span>
                <span
                  v-if="event.content"
                  class="text-caption"
                >
                  {{ event.content }}
                </span>
              </template>
            </template>
            <span
              v-else
              class="font-weight-bold text-truncate"
            >
              {{ event.title }}
            </span>
            <v-tooltip
              v-if="isBreak(event)"
              activator="parent"
              location="top"
              open-delay="150"
              :disabled="dragging"
            >
              <div class="font-weight-bold">
                {{ event.title }}
              </div>
              <div v-if="event.content">
                {{ event.content }}
              </div>
            </v-tooltip>
            <!-- Visit blocks: same hover tooltip as the overlays, address as the last row. -->
            <v-tooltip
              v-else-if="event.assignmentId !== undefined"
              activator="parent"
              location="top"
              open-delay="150"
              :disabled="dragging"
            >
              <div class="font-weight-bold">
                {{ event.title }}
              </div>
              <div v-if="event.content">
                {{ event.content }}
              </div>
              <div v-if="event.address">
                <v-icon
                  size="14"
                  start
                >
                  mdi-map-marker
                </v-icon>{{ event.address }}
              </div>
            </v-tooltip>
            <!-- Existing-shift blocks: flagged as committed shifts; editable ones behave like
                 visits, the rest explain why they are read-only. -->
            <v-tooltip
              v-else-if="event.existingShiftId !== undefined"
              activator="parent"
              location="top"
              open-delay="150"
              :disabled="dragging"
            >
              <div class="font-weight-bold">
                {{ event.title }}
              </div>
              <div v-if="event.content">
                {{ event.content }}
              </div>
              <div>{{ t('routePlan.shiftEdit.existingShift') }}</div>
              <div v-if="event.moved">
                {{ t('routePlan.shiftEdit.movedTooltip', { time: event.originalTime }) }}
              </div>
              <div v-if="event.originalCaregiverName">
                {{ t('routePlan.shiftEdit.originalCaregiver',
                     { name: event.originalCaregiverName }) }}
              </div>
              <div v-if="event.unrouted">
                {{ t('routePlan.shiftEdit.keptTooltip') }}
              </div>
              <div v-if="event.unplanned">
                {{ t('routePlan.shiftEdit.unplannedTooltip') }}
              </div>
              <div v-if="!event.draggable">
                {{ t('routePlan.shiftEdit.notMovable') }}
              </div>
              <div v-if="event.address">
                <v-icon
                  size="14"
                  start
                >
                  mdi-map-marker
                </v-icon>{{ event.address }}
              </div>
            </v-tooltip>
          </button>
        </template>
      </vue-cal>
    </div>
  </div>
</template>
<style scoped>
.route-plan-calendar :deep(.vuecal) {
  --vuecal-primary-color: #fff;
  --vuecal-min-schedule-size: 160px;
  background-color: #fff;
  height: calc(100dvh - 280px);
  min-height: 600px;
}

@media (max-width: 600px) {
  .route-plan-calendar :deep(.vuecal) {
    height: calc(100dvh - 230px);
    min-height: 420px;
  }
}

.route-plan-calendar__progress {
  /* Vuetify's default v-progress-linear height. */
  height: 4px;
}

/* vue-cal 5 RC width formula needs the single-column override on the day scrollable. */
.route-plan-calendar :deep(.vuecal__scrollable--day-view) {
  --vuecal-grid-columns: 1;
}

.route-plan-calendar :deep(.vuecal__schedule--heading) {
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: rgba(0, 0, 0, .87);
  background-color: #fff;
  overflow: hidden;
}

.route-plan-calendar__schedule-name {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  text-align: center;
  font-size: .8125rem;
  line-height: 1.2;
  padding: 0 4px;
  overflow-wrap: break-word;
  white-space: normal;
  cursor: pointer;
  width: 100%;
}

.route-plan-calendar__schedule-edit-icon {
  opacity: 0;
  transition: opacity .15s;
}

.route-plan-calendar__schedule-name:hover .route-plan-calendar__schedule-edit-icon,
.route-plan-calendar__schedule-name:focus-visible .route-plan-calendar__schedule-edit-icon {
  opacity: .7;
}

/* Horizontal guide line per hour, aligned with the time column steps. */
.route-plan-calendar :deep(.vuecal__cell) {
  background-image: repeating-linear-gradient(to bottom,
  transparent 0,
  transparent calc(var(--route-plan-hour-height) - 1px),
  rgba(0, 0, 0, .08) calc(var(--route-plan-hour-height) - 1px),
  rgba(0, 0, 0, .08) var(--route-plan-hour-height));
  /* Empty slots are clickable (place a pending visit), so they invite a click. */
  cursor: pointer;
}

/* Vertical divider between day and caregiver columns (and their headings). */
.route-plan-calendar :deep(.vuecal__cell),
.route-plan-calendar :deep(.vuecal__schedule--cell),
.route-plan-calendar :deep(.vuecal__schedule--heading) {
  border-right: 1px solid rgba(0, 0, 0, .1);
}

.route-plan-calendar :deep(.vuecal__event) {
  cursor: pointer;
  border-radius: 4px;
  overflow: hidden;
  /* Opaque base so cascaded (stacked) events occlude the ones below. */
  background-color: #fff;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, .06);
}

/* An existing shift with a manual move proposed: accent edge as the "modificado" badge. */
.route-plan-calendar :deep(.vuecal__event.route-plan-existing-shift.is-moved) {
  border-left: 3px solid #ff9800;
}

/* A shift the solve never planned around: dashed amber outline, "not part of the route". */
.route-plan-calendar :deep(.vuecal__event.route-plan-existing-shift.is-unplanned) {
  border: 1px dashed #f57c00;
}

/* The lunch break is an informational overlay: flat (no shadow); it hovers for a tooltip and
   forwards clicks to placement (handled in onEventActivate). */

.route-plan-calendar :deep(.vuecal__event.route-plan-break) {
  box-shadow: none;
}

/* Hours outside a caregiver's availability: shaded so the free slots read as the white gaps. */
.route-plan-calendar :deep(.vuecal__special-hours.route-plan-off-hours) {
  background-color: rgba(96, 96, 96, .13);
}

.route-plan-calendar__event {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  height: 100%;
  padding: 2px 4px;
  text-align: left;
  color: inherit;
  container-type: size;
}

/* Compact (sub-hour) blocks: one line where the name truncates but the time stays visible. */
.route-plan-calendar__event-line {
  display: flex;
  gap: 4px;
  align-items: baseline;
  width: 100%;
  min-width: 0;
}

.route-plan-calendar__event-line .text-caption {
  flex-shrink: 0;
}

@container (max-height: 44px) {
  /* Hide only the second-line time (direct child); the compact one-line time
     (inside __event-line) must stay visible. */

  .route-plan-calendar__event > .text-caption {
    display: none !important;
  }
}

@container (max-width: 110px) {
  .route-plan-calendar__event .text-caption {
    display: none !important;
  }
}
</style>
